import React from 'react';

import i18n from 'util/i18n';

import { Summary, DateSummary } from 'components/Summary';

import { SUBSTANCE_USE, SUBSTANCE_USE_DRUGS_VOLUNTARY } from 'config/formSections/substanceUse';
import { formConfig } from 'config/forms';
import { getNumberOfYearsString } from 'helpers/text';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { drugsVoluntaryTreatmentAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import Field from 'components/Form/Field';
import { isPVQForm } from 'helpers/form';
import VoluntaryTreatment from './VoluntaryTreatment';

const sectionConfig = {
  key: SUBSTANCE_USE_DRUGS_VOLUNTARY.key,
  section: SUBSTANCE_USE.name,
  store: SUBSTANCE_USE.store,
  subsection: SUBSTANCE_USE_DRUGS_VOLUNTARY.name,
  storeKey: SUBSTANCE_USE_DRUGS_VOLUNTARY.storeKey,
};

export class VoluntaryTreatments extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateList = this.updateList.bind(this);
    this.updateTreatmentVoluntary = this.updateTreatmentVoluntary.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (updateValues) => {
    this.props.onUpdate(this.storeKey, {
      TreatmentVoluntary: this.props.TreatmentVoluntary,
      List: this.props.List,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...updateValues,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateTreatmentVoluntary = (values) => {
    this.update({
      TreatmentVoluntary: values,
      List: values.value === 'Yes' ? this.props.List : { items: [], branch: {} },
      SummaryComments: values.value === 'Yes' ? this.props.SummaryComments : {},
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const range = DateSummary(o.TreatmentDates);
    const name = (o.TreatmentProvider || {}).value;

    return Summary({
      type: i18n.t('substance.drugs.voluntary.collection.itemType'),
      index,
      left: name,
      right: range,
      placeholder: i18n.t('substance.drugs.voluntary.collection.summary'),
    });
  };

  render() {
    const {
      formType, errors, peopleWhoKnowYou, List
    } = this.props;
    const formTypeConfig = formType && formConfig[formType];
    const years = formTypeConfig && formTypeConfig.SUBSTANCE_DRUG_TREATMENT_YEARS;
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, drugsVoluntaryTreatmentAddressNameFields);

    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    let branchLabelCopy;
    let appendTitleCopy;
    if (years === 'EVER') {
      branchLabelCopy = i18n.t('substance.drugs.heading.voluntaryTreatmentsEver');
      appendTitleCopy = i18n.t('substance.drugs.voluntary.collection.appendTitleEver');
    } else {
      const numberOfYearsString = getNumberOfYearsString(years);
      branchLabelCopy = i18n.t('substance.drugs.heading.voluntaryTreatments', {
        numberOfYearsString,
      });
      appendTitleCopy = i18n.t('substance.drugs.voluntary.collection.appendTitle', {
        numberOfYearsString,
      });
    }

    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const isPvq = isPVQForm(this.props.formType);

    return isPvq ? (
      <div
        className="section-content voluntary-treatments"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_DRUGS_VOLUNTARY.key}
      >
        <h1 className="section-header">{i18n.t('substance.pvq.subsection.drugs.voluntary')}</h1>
        <Branch
          name="TreatmentVoluntary"
          label={i18n.t('substance.pvq.drugs.heading.voluntaryTreatments')}
          labelSize="h4"
          className="treatment-voluntary"
          {...this.props.TreatmentVoluntary}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateTreatmentVoluntary}
          scrollIntoView={this.props.scrollIntoView}
          attribute="TreatmentVoluntary"
        />
        <Show when={this.props.TreatmentVoluntary.value === 'Yes'}>
          <Accordion
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.pvq.drugs.voluntary.collection.description')}
            appendTitle={i18n.t('substance.pvq.drugs.voluntary.collection.appendTitle')}
            appendLabel={i18n.t('substance.pvq.drugs.voluntary.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <VoluntaryTreatment
              name="Item"
              bind
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="List"
              formType={this.props.formType}
            />
          </Accordion>
        </Show>
      </div>
    ) : (
      <div
        className="section-content voluntary-treatments"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_DRUGS_VOLUNTARY.key}
      >
        <h1 className="section-header">{i18n.t('substance.subsection.drugs.voluntary')}</h1>
        <Branch
          name="TreatmentVoluntary"
          label={branchLabelCopy}
          labelSize="h4"
          className="treatment-voluntary"
          {...this.props.TreatmentVoluntary}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateTreatmentVoluntary}
          scrollIntoView={this.props.scrollIntoView}
          attribute="TreatmentVoluntary"
        />
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
        <Show when={this.props.TreatmentVoluntary.value === 'Yes'}>
          <Accordion
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.drugs.voluntary.collection.description')}
            appendTitle={appendTitleCopy}
            appendLabel={i18n.t('substance.drugs.voluntary.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <VoluntaryTreatment
              name="Item"
              bind
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="List"
            />
          </Accordion>
          <Comments
            title={i18n.t('substance.comments.summary')}
            name="SummaryComments"
            className="summary-comment"
            attribute="SummaryComments"
            {...this.props.SummaryComments}
            onUpdate={this.updateSummaryComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

VoluntaryTreatments.defaultProps = {
  TreatmentVoluntary: {},
  List: {},
  Comments: {},
  SummaryComments: {},
  onError: (value, arr) => arr,
  section: 'substance',
  subsection: 'drugs/voluntary',
  addressBooks: {},
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
  formType: '',
};

export default connectSubsection(VoluntaryTreatments, sectionConfig);
